import { cn } from '@/shared/utils/cn';
import type { CatalogCardMenuItem, CatalogCardMenuItemContext } from '../../types';
import type { NormalizedCatalogCardTag } from '../../utils/catalogCardTagUtils';
import { Badge } from '@/modules/Badge';
import { Button } from '@/modules/Button';
import { Checkbox } from '@/modules/Checkbox';
import { List, ListItem, ListItemCheckbox, ListSeparator } from '@/modules/Lists';
import { menuPanelBox, stopCardClick } from './catalogCardMenuPanelUtils';

function menuItemToContext(
  item: Extract<CatalogCardMenuItem, { label: string }>
): CatalogCardMenuItemContext {
  const { onSelect: _onSelect, ...rest } = item;
  return rest;
}

function renderMenuRows(items: CatalogCardMenuItem[], onDismiss?: () => void) {
  return items.map((item, index) => {
    if (item.type === 'separator') {
      return <ListSeparator key={`sep-${index}`} />;
    }
    return (
      <ListItem
        key={item.id ?? `item-${index}`}
        color={item.color ?? 'blue'}
        current={item.current}
        icon={item.icon}
        onClick={(ev) => {
          stopCardClick(ev);
          item.onSelect?.(menuItemToContext(item));
          onDismiss?.();
        }}
      >
        {item.label}
      </ListItem>
    );
  });
}

/**
 * Panel de lista para menú contextual. `onDismiss` debe cerrar el `Popup` padre: el panel usa
 * `stopPropagation` y el clic no llega al handler `closeOnClick` del popup.
 */
export function OptionsMenuPanel({
  items,
  onDismiss,
}: {
  items: CatalogCardMenuItem[];
  onDismiss?: () => void;
}) {
  return (
    <div className={menuPanelBox} onClick={stopCardClick}>
      <List padding="sm">{renderMenuRows(items, onDismiss)}</List>
    </div>
  );
}

const DEFAULT_SELECT_ALL_LABEL = 'Seleccionar todas';

/**
 * Figma Cards `238:14855` (List): `p-[space_8]`, `gap-[space_8]` entre ítems / divisor / botón;
 * divisor `BaseH`: `p-[space_4]` alrededor de la línea.
 */

/**
 * Bloque de checkboxes alineado a `tags` del catálogo (mismas cadenas que los badges). No cierra el `Popup` al cambiar.
 * Filas tipo `ListItemCheckbox`; fondo de marca cuando la opción está marcada (o *seleccionar todas* activo/indeterminado).
 * El padding del panel lo aplica `PrimaryMenuPanel` (`ui-p-8`).
 */
function PrimaryTagsCheckboxBlock({
  tags,
  selectedTags,
  onChange,
  selectAllLabel = DEFAULT_SELECT_ALL_LABEL,
  showSelectAll = false,
}: {
  tags: NormalizedCatalogCardTag[];
  selectedTags: string[];
  onChange: (next: string[]) => void;
  selectAllLabel?: string;
  /** @defaultValue false (Figma 233:13284 sin fila *seleccionar todas*). */
  showSelectAll?: boolean;
}) {
  const selectedSet = new Set(selectedTags);
  const selectableTags = tags.filter((t) => !t.disabled);
  const selectableLabels = selectableTags.map((t) => t.label);
  const allChecked =
    selectableLabels.length > 0 && selectableLabels.every((t) => selectedSet.has(t));
  const someChecked = selectableLabels.some((t) => selectedSet.has(t));
  const indeterminate = someChecked && !allChecked;

  const toggleTag = (label: string, checked: boolean) => {
    const next = new Set(selectedTags);
    if (checked) next.add(label);
    else next.delete(label);
    onChange(selectableLabels.filter((t) => next.has(t)));
  };

  return (
    <div className="ui-flex ui-flex-col ui-gap-8" role="group" aria-label="Etiquetas">
      {showSelectAll && selectableLabels.length > 1 ? (
        <div className="ui-border-b ui-border-subtle ui-pb-8">
          <div
            className={cn(
              'ui-flex ui-items-center ui-gap-8 ui-rounded-control-sm ui-px-8 ui-py-6',
              (allChecked || indeterminate) && 'ui-bg-fill-brand-default'
            )}
          >
            <Checkbox
              checked={allChecked}
              indeterminate={indeterminate}
              onChange={(checked) => {
                onChange(checked ? [...selectableLabels] : []);
              }}
              size="md"
            />
            <span
              className={cn(
                'ui-cursor-pointer ui-text-body-sm-regular ui-font-body-sm-medium ui-leading-body-sm-regular ui-tracking-body-sm',
                allChecked || indeterminate ? 'ui-text-on-color' : 'ui-text-primary'
              )}
              onClick={(e) => {
                stopCardClick(e);
                onChange(allChecked ? [] : [...selectableLabels]);
              }}
            >
              {selectAllLabel}
            </span>
          </div>
        </div>
      ) : null}
      {tags.map((tag, index) => {
        const { label, disabled, panelBadge } = tag;
        const checked = selectedSet.has(label);
        return (
          <div
            key={`${label}-${index}`}
            className={cn(
              'ui-flex ui-min-w-0 ui-items-center ui-gap-8',
              checked && !disabled && 'ui-rounded-control-sm ui-bg-fill-brand-default'
            )}
          >
            <ListItemCheckbox
              title={label}
              checked={checked}
              disabled={disabled}
              disableHover
              onChange={(next) => {
                if (!disabled) toggleTag(label, next);
              }}
              className={cn('ui-min-w-0 ui-flex-1', checked && !disabled && 'ui-bg-transparent')}
              titleClassName={cn(
                'ui-truncate',
                checked && !disabled ? 'ui-text-on-color' : disabled ? 'ui-text-tertiary' : undefined
              )}
            />
            {panelBadge?.label?.trim() ? (
              <Badge
                label={panelBadge.label}
                color={panelBadge.color ?? 'danger'}
                theme="soft"
                size="sm"
                className="ui-mr-8 ui-shrink-0"
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Panel del CTA principal: pie **arriba** (p. ej. *Cerrar*), debajo las opciones (checkboxes de `tags` y lista `items`).
 * `onDismiss` debe cerrar el `Popup` padre.
 */
export function PrimaryMenuPanel({
  items = [],
  tags,
  selectedTags = [],
  onTagsChange,
  selectAllLabel,
  tagsShowSelectAll = false,
  footer,
  maxHeightClass = 'ui-h-[215px]',
  onDismiss,
}: {
  items?: CatalogCardMenuItem[];
  /** Mismas etiquetas que los badges; checkboxes bajo el pie. */
  tags?: NormalizedCatalogCardTag[];
  selectedTags?: string[];
  onTagsChange?: (next: string[]) => void;
  selectAllLabel?: string;
  /** @defaultValue false */
  tagsShowSelectAll?: boolean;
  footer?: { label: string; onClick?: (footer?: { label: string }) => void };
  maxHeightClass?: string;
  onDismiss?: () => void;
}) {
  const showTags = Boolean(tags && tags.length > 0 && onTagsChange);
  const showList = items.length > 0;
  const showTagsDivider = showTags && Boolean(footer);

  return (
    <div
      className={cn(
        'ui-flex ui-w-52 ui-flex-col-reverse ui-gap-8 ui-overflow-hidden ui-p-8',
        maxHeightClass,
        menuPanelBox
      )}
      onClick={stopCardClick}
    >
      {footer ? (
        <div className="ui-shrink-0">
          <Button
            color="secondary"
            theme="solid"
            size="xxs"
            fullWidth
            onClick={(e) => {
              stopCardClick(e);
              footer.onClick?.({ label: footer.label });
              onDismiss?.();
            }}
          >
            {footer.label}
          </Button>
        </div>
      ) : null}
      {showTagsDivider ? (
        <div className="ui-shrink-0 ui-py-4" aria-hidden>
          <div className="ui-border-b ui-border-subtle" />
        </div>
      ) : null}
      {showTags && tags && onTagsChange ? (
        <div className="ui-min-h-0 ui-flex-1 ui-overflow-y-auto">
          <PrimaryTagsCheckboxBlock
            tags={tags}
            selectedTags={selectedTags}
            onChange={onTagsChange}
            selectAllLabel={selectAllLabel}
            showSelectAll={tagsShowSelectAll}
          />
        </div>
      ) : null}
      {showList ? (
        <div className="ui-min-h-0 ui-flex-1 ui-overflow-y-auto">
          <List padding="none">{renderMenuRows(items, onDismiss)}</List>
        </div>
      ) : null}
    </div>
  );
}
